import type { LogbookRecord } from '@/hooks/use-logbook';
import { makeAutoObservable } from 'mobx';

export type EditableLogbookCurrencyField =
  | 'taxes_paid'
  | 'dmv_fees_paid'
  | 'less_penalties'
  | 'ever_fee_refund';

class LogbookStore {
  // IMPORTANT NOTE: this will be the normalized source of truth for the logbook records
  // When you make changes to the logbook records, you need to make sure to update this
  // If you want changes to the derived logbook records data below, trigger a re-calculation
  private logbookRecordsIndexedById: Record<
    LogbookRecord['id'],
    LogbookRecord
  > = {};

  // IMPORTANT note: these are derived from the source of truth
  // DO NOT MAKE CHANGES TO THESE DIRECTLY
  private logbookRecords: LogbookRecord[] = [];
  private logbookRecordsByCdkId: Record<
    LogbookRecord['cdk_deal_id'],
    LogbookRecord[]
  > = {};

  constructor() {
    makeAutoObservable(this);
  }

  getLogbookRecords() {
    return this.logbookRecords;
  }

  getLogbookRecordById(id: LogbookRecord['id']): LogbookRecord | null {
    return this.logbookRecordsIndexedById[id];
  }

  setLogbookRecords(logbookRecords: LogbookRecord[]) {
    this.logbookRecords = logbookRecords;
    this.logbookRecordsIndexedById = logbookRecords.reduce(
      (acc, record) => {
        acc[record.id] = record;
        return acc;
      },
      {} as Record<LogbookRecord['id'], LogbookRecord>,
    );

    this.logbookRecordsByCdkId = logbookRecords.reduce(
      (acc, record) => {
        acc[record.cdk_deal_id] = [...(acc[record.cdk_deal_id] || []), record];
        return acc;
      },
      {} as Record<LogbookRecord['cdk_deal_id'], LogbookRecord[]>,
    );
  }

  updateLogbookRecordById(
    id: LogbookRecord['id'],
    logbookRecord: LogbookRecord,
  ) {
    this.logbookRecordsIndexedById[id] = logbookRecord;
    this.logbookRecords = this.logbookRecords.map((record) => {
      if (record.id === id) {
        return logbookRecord;
      }
      return record;
    });
    this.logbookRecordsByCdkId = this.logbookRecords.reduce(
      (acc, record) => {
        acc[record.cdk_deal_id] = [...(acc[record.cdk_deal_id] || []), record];
        return acc;
      },
      {} as Record<LogbookRecord['cdk_deal_id'], LogbookRecord[]>,
    );
  }

  getLogbookRecordsByCdkId(cdkId: LogbookRecord['cdk_deal_id']) {
    return this.logbookRecordsByCdkId[cdkId];
  }

  // private resyncLogbookRecordsToSourceOfTruth() {
  //   this.setLogbookRecords(this.logbookRecords);
  // }

  updateTaxesPaid({
    id,
    taxesPaid,
  }: {
    id: LogbookRecord['id'];
    taxesPaid: LogbookRecord['taxes_paid'];
  }) {
    const logbookRecord = this.getLogbookRecordById(id);

    if (!logbookRecord) {
      return;
    }

    logbookRecord.taxes_paid = taxesPaid;
  }

  _resyncLogbookRecordsToSourceOfTruth(id: LogbookRecord['id']) {
    const logbookRecord = this.getLogbookRecordById(id);
    if (!logbookRecord) {
      return;
    }
    this.logbookRecords = this.logbookRecords.map((record) => {
      if (record.id === id) {
        return logbookRecord;
      }
      return record;
    });
  }

  updateCurrencyField({
    id,
    fieldName,
    value,
  }: {
    id: LogbookRecord['id'];
    fieldName: EditableLogbookCurrencyField;
    value: number;
  }) {
    const logbookRecord = this.getLogbookRecordById(id);

    if (!logbookRecord) {
      return;
    }

    logbookRecord[fieldName] = value;

    this._resyncLogbookRecordsToSourceOfTruth(id);
  }
}

export const logbookStore = new LogbookStore();
